import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import {
  assignBodyZonesToCollection,
  assignPiercingTypesToCollection,
  listItemCollections,
  type ItemCollection,
} from '../services/itemCollectionsApi';

interface BodyZoneRow {
  id: string;
  name: string;
  itemCollectionId: string;
  itemCollectionName?: string;
}

interface PiercingTypeRow {
  id: string;
  name: string;
  bodyZoneId?: string | null;
  bodyZoneName?: string | null;
  itemCollectionId: string;
}

export function ItemCollectionAssociationsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<ItemCollection | null>(null);
  const [collections, setCollections] = useState<ItemCollection[]>([]);
  const [zones, setZones] = useState<BodyZoneRow[]>([]);
  const [allZones, setAllZones] = useState<BodyZoneRow[]>([]);
  const [types, setTypes] = useState<PiercingTypeRow[]>([]);
  const [allTypes, setAllTypes] = useState<PiercingTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoneToAdd, setZoneToAdd] = useState('');
  const [typeToAdd, setTypeToAdd] = useState('');
  const [moveTarget, setMoveTarget] = useState('');

  const token = () => localStorage.getItem('token');

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [collRes, list, zonesHere, zonesAll, typesHere, typesAll] = await Promise.all([
        fetch(getApiUrl(`/api/admin/item-collections/${id}`), {
          headers: { Authorization: `Bearer ${token()}` },
        }).then((r) => r.json()),
        listItemCollections({ limit: 1000 }),
        fetch(getApiUrl(`/api/admin/body-zones?limit=1000&itemCollectionId=${id}`), {
          headers: { Authorization: `Bearer ${token()}` },
        }).then((r) => r.json()),
        fetch(getApiUrl('/api/admin/body-zones?limit=1000'), {
          headers: { Authorization: `Bearer ${token()}` },
        }).then((r) => r.json()),
        fetch(getApiUrl(`/api/admin/piercing-types?limit=1000&itemCollectionId=${id}`), {
          headers: { Authorization: `Bearer ${token()}` },
        }).then((r) => r.json()),
        fetch(getApiUrl('/api/admin/piercing-types?limit=1000'), {
          headers: { Authorization: `Bearer ${token()}` },
        }).then((r) => r.json()),
      ]);
      setCollection(collRes.data?.itemCollection || null);
      setCollections(list.itemCollections.filter((c) => c.id !== id));
      setZones(zonesHere.data?.bodyZones || []);
      setAllZones(zonesAll.data?.bodyZones || []);
      setTypes(typesHere.data?.piercingTypes || []);
      setAllTypes(typesAll.data?.piercingTypes || []);
    } catch {
      setError(t('catalog.itemCollections.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const addZone = async () => {
    if (!id || !zoneToAdd) return;
    await assignBodyZonesToCollection(id, [zoneToAdd]);
    setZoneToAdd('');
    load();
  };

  const addType = async () => {
    if (!id || !typeToAdd) return;
    try {
      await assignPiercingTypesToCollection(id, [typeToAdd]);
      setTypeToAdd('');
      load();
    } catch (err: any) {
      setError(err?.message || t('catalog.itemCollections.saveError'));
    }
  };

  const moveZone = async (zoneId: string) => {
    if (!moveTarget) return;
    await assignBodyZonesToCollection(moveTarget, [zoneId]);
    load();
  };

  const moveType = async (typeId: string) => {
    if (!moveTarget) return;
    try {
      await assignPiercingTypesToCollection(moveTarget, [typeId]);
      load();
    } catch (err: any) {
      setError(err?.message || t('catalog.itemCollections.saveError'));
    }
  };

  if (loading && !collection) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const otherZones = allZones.filter((z) => z.itemCollectionId !== id);
  const otherTypes = allTypes.filter((p) => p.itemCollectionId !== id);

  return (
    <div className="p-6">
      <button onClick={() => navigate('/item-collections')} className="btn-secondary inline-flex items-center space-x-2 mb-6">
        <ArrowLeft className="w-4 h-4" />
        <span>{t('catalog.itemCollections.back')}</span>
      </button>
      <h1 className="text-2xl font-display font-bold mb-2">
        {t('catalog.itemCollections.associations')} — {collection?.name}
      </h1>
      <p className="text-textSecondary mb-6">{t('catalog.itemCollections.associationsHint')}</p>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}

      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">{t('catalog.itemCollections.moveTo')}</label>
        <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} className="input-field max-w-md">
          <option value="">{t('catalog.itemCollections.selectCollection')}</option>
          {collections.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-3">{t('catalog.itemCollections.zones')}</h2>
          <div className="flex space-x-2 mb-3">
            <select value={zoneToAdd} onChange={(e) => setZoneToAdd(e.target.value)} className="input-field flex-1">
              <option value="">{t('catalog.itemCollections.selectZone')}</option>
              {otherZones.map((z) => (
                <option key={z.id} value={z.id}>{z.name} ({z.itemCollectionName || z.itemCollectionId})</option>
              ))}
            </select>
            <button onClick={addZone} className="btn-primary"><Plus className="w-4 h-4" /></button>
          </div>
          <ul className="divide-y">
            {zones.map((z) => (
              <li key={z.id} className="py-2 flex items-center justify-between text-sm">
                <span>{z.name}</span>
                <button onClick={() => moveZone(z.id)} disabled={!moveTarget} className="text-primary-600 disabled:text-gray-300">
                  {t('catalog.itemCollections.move')}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-3">{t('catalog.itemCollections.types')}</h2>
          <div className="flex space-x-2 mb-3">
            <select value={typeToAdd} onChange={(e) => setTypeToAdd(e.target.value)} className="input-field flex-1">
              <option value="">{t('catalog.itemCollections.selectType')}</option>
              {otherTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.bodyZoneName ? ` — ${p.bodyZoneName}` : ''}
                </option>
              ))}
            </select>
            <button onClick={addType} className="btn-primary"><Plus className="w-4 h-4" /></button>
          </div>
          <ul className="divide-y">
            {types.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                <span>{p.name}{p.bodyZoneName ? ` (${p.bodyZoneName})` : ''}</span>
                <button onClick={() => moveType(p.id)} disabled={!moveTarget} className="text-primary-600 disabled:text-gray-300">
                  {t('catalog.itemCollections.move')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
